import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Youtube, Instagram, Music2, ChevronLeft, ChevronRight, X, Play, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PageWrap } from "@/components/PageWrap";
import { SectionHeading } from "@/components/SectionHeading";
import { getGallery, getYoutubeVideos, resolveImageUrl } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const YOUTUBE_CHANNEL_URL = "https://www.youtube.com/@Driftex606";
const YOUTUBE_VIDEOS_URL = "https://www.youtube.com/@Driftex606/videos";

const PLATFORMS = [
  { icon: Youtube, name: "YouTube", handle: "@Driftex606", descKey: "media.platforms.youtube", href: YOUTUBE_CHANNEL_URL },
  { icon: Instagram, name: "Instagram", handle: "@driftex_", descKey: "media.platforms.instagram", href: "https://www.instagram.com/driftex_/?hl=cs" },
  { icon: Music2, name: "TikTok", handle: "@filipvimpel", descKey: "media.platforms.tiktok", href: "https://www.tiktok.com/@filipvimpel" },
];

const fmtDate = (iso, locale) =>
  iso ? new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" }) : "";

/* ---------------- YouTube video lightbox (fullscreen player) ---------------- */

const VideoLightbox = ({ video, onClose }) => {
  const { t, dateLocale } = useLang();
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="media-video-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#07080A]/95 p-4 backdrop-blur-md sm:p-8"
      onClick={onClose}
    >
      <div className="scanlines pointer-events-none absolute inset-0 opacity-40" />
      {/* HUD corners */}
      <div className="pointer-events-none absolute left-4 top-4 h-10 w-10 border-l-2 border-t-2 border-[#1EA7FF]/60" />
      <div className="pointer-events-none absolute right-4 top-4 h-10 w-10 border-r-2 border-t-2 border-[#1EA7FF]/60" />
      <div className="pointer-events-none absolute bottom-4 left-4 h-10 w-10 border-b-2 border-l-2 border-[#B7FF2A]/50" />
      <div className="pointer-events-none absolute bottom-4 right-4 h-10 w-10 border-b-2 border-r-2 border-[#B7FF2A]/50" />

      <motion.div
        initial={{ scale: 0.92, y: 24, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.94, y: 16, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-6xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="clip-corner neon-edge overflow-hidden border border-white/15 bg-black">
          <div className="aspect-video w-full">
            <iframe
              data-testid="media-video-lightbox-iframe"
              src={`https://www.youtube.com/embed/${video.video_id}?autoplay=1&rel=0`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="h-full w-full"
            />
          </div>
        </div>
        <div className="mt-3 flex items-start justify-between gap-4 px-1">
          <div>
            <div className="font-display text-xl uppercase tracking-[0.06em] text-white sm:text-2xl">
              {video.title}
            </div>
            <div className="font-mono text-xs text-white/50">{fmtDate(video.published_at, dateLocale)}</div>
          </div>
          <a
            href={`https://www.youtube.com/watch?v=${video.video_id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="media-video-lightbox-youtube-link"
            className="flex shrink-0 items-center gap-1.5 font-mono text-xs uppercase tracking-[0.14em] text-white/45 transition-colors hover:text-[#B7FF2A]"
          >
            <ExternalLink className="h-3.5 w-3.5" /> YouTube
          </a>
        </div>
        <button
          data-testid="media-video-lightbox-close"
          onClick={onClose}
          className="absolute -right-2 -top-12 flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white transition-colors hover:bg-[#FF3B3B] sm:-right-12 sm:-top-2"
          aria-label={t("media.closeVideo")}
        >
          <X className="h-5 w-5" />
        </button>
      </motion.div>
    </motion.div>
  );
};

/* ---------------- page ---------------- */

export default function Media() {
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [videosError, setVideosError] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const { t, dateLocale } = useLang();

  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const isOpen = lightboxIndex !== null;

  useEffect(() => {
    getYoutubeVideos()
      .then((d) => setVideos(d.videos || []))
      .catch(() => setVideosError(true))
      .finally(() => setVideosLoading(false));
    getGallery()
      .then(setGallery)
      .catch(() => {})
      .finally(() => setGalleryLoading(false));
  }, []);

  const next = useCallback(
    () => setLightboxIndex((i) => (i === null || gallery.length === 0 ? null : (i + 1) % gallery.length)),
    [gallery.length]
  );
  const prev = useCallback(
    () =>
      setLightboxIndex((i) =>
        i === null || gallery.length === 0 ? null : (i - 1 + gallery.length) % gallery.length
      ),
    [gallery.length]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, next, prev]);

  return (
    <PageWrap testId="media-page">
      <section className="relative overflow-hidden pt-36 pb-12">
        <div className="hero-glow absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B7FF2A]">// {t("media.kicker")}</div>
            <h1 data-testid="media-headline" className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-[0.03em] sm:text-8xl">
              {t("media.title")}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ============ YOUTUBE 3x3 GRID ============ */}
      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <SectionHeading kicker="YouTube × @Driftex606" title={t("media.videosTitle")} testId="media-videos-heading" />
          <a
            href={YOUTUBE_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="media-videos-channel-link"
            className="group inline-flex items-center gap-2 font-display text-lg uppercase tracking-[0.14em] text-[#1EA7FF]"
          >
            <Youtube className="h-5 w-5" /> {t("media.channel")}
            <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>

        {videosLoading ? (
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="media-videos-loading">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="clip-corner aspect-video animate-pulse border border-white/5 bg-[#0F1217]" />
            ))}
          </div>
        ) : videosError || videos.length === 0 ? (
          <div
            data-testid="media-videos-error"
            className="clip-corner mt-10 flex flex-col items-center gap-4 border border-white/10 bg-[#0F1217] p-10 text-center"
          >
            <Youtube className="h-10 w-10 text-white/25" />
            <p className="text-sm text-white/50">
              {t("media.videosError")}
            </p>
            <a
              href={YOUTUBE_VIDEOS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="light-sweep clip-corner-sm inline-flex items-center gap-2 bg-[#1EA7FF] px-6 py-3 font-display text-sm uppercase tracking-[0.14em] text-[#07080A] hover:brightness-110"
            >
              <Youtube className="h-4 w-4" /> {t("media.openYoutube")}
            </a>
          </div>
        ) : (
          <>
            <div data-testid="media-videos-grid" className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {videos.map((v, i) => (
                <motion.button
                  key={v.video_id}
                  data-testid={`media-video-card-${i}`}
                  onClick={() => setActiveVideo(v)}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: 0.06 * (i % 3), ease: [0.22, 1, 0.36, 1] }}
                  className="clip-corner group relative overflow-hidden border border-white/10 bg-[#0F1217] text-left transition-colors duration-200 hover:border-[#1EA7FF]/50 hover:neon-edge focus-visible:ring-2 focus-visible:ring-[#1EA7FF]"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={v.thumbnail}
                      alt={v.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#07080A]/90 via-[#07080A]/20 to-transparent" />
                    {/* HUD index */}
                    <span className="absolute left-3 top-3 bg-[#07080A]/70 px-2 py-0.5 font-mono text-[10px] tracking-[0.2em] text-[#B7FF2A]">
                      {String(i + 1).padStart(2, "0")} / 09
                    </span>
                    {/* play button */}
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-[#07080A]/60 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:border-[#B7FF2A] group-hover:bg-[#B7FF2A] group-hover:text-[#07080A]">
                        <Play className="ml-0.5 h-6 w-6 fill-current" />
                      </span>
                    </span>
                    {/* title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="line-clamp-2 font-display text-lg uppercase leading-tight tracking-[0.05em] text-white">
                        {v.title}
                      </div>
                      <div className="mt-1 font-mono text-[11px] text-white/50">{fmtDate(v.published_at, dateLocale)}</div>
                    </div>
                  </div>
                  {/* accent rail */}
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] overflow-hidden">
                    <div className="h-full w-0 bg-[#1EA7FF] transition-[width] duration-500 ease-out group-hover:w-full" />
                  </div>
                </motion.button>
              ))}
            </div>

            {/* older videos CTA */}
            <div className="mt-10 flex justify-center">
              <a
                href={YOUTUBE_VIDEOS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="media-older-videos-button"
                className="light-sweep clip-corner group relative inline-flex items-center gap-3 border border-[#B7FF2A]/40 bg-[#B7FF2A]/10 px-8 py-4 font-display text-lg uppercase tracking-[0.16em] text-[#B7FF2A] transition-colors duration-200 hover:bg-[#B7FF2A]/20 hover:neon-edge-acid"
              >
                <Youtube className="h-5 w-5" />
                <span className="rgb-split" data-text={t("media.olderVideos")}>
                  {t("media.olderVideos")}
                </span>
                <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </>
        )}
      </section>

      {/* ============ PHOTO GALLERY (dynamic from DB) ============ */}
      <section className="border-t border-white/10 bg-[#0B0D10] py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionHeading kicker={t("media.galleryKicker")} title={t("media.galleryTitle")} accent="acid" testId="media-gallery-heading" />

          {galleryLoading ? (
            <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="media-gallery-loading">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`clip-corner-sm animate-pulse border border-white/5 bg-[#0F1217] ${i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}
                />
              ))}
            </div>
          ) : gallery.length === 0 ? (
            <div className="mt-10 py-14 text-center text-white/40" data-testid="media-gallery-empty">
              {t("media.galleryEmpty")}
            </div>
          ) : (
            <div data-testid="media-gallery-grid" className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
              {gallery.map((g, i) => (
                <motion.button
                  key={g.id}
                  data-testid={`media-gallery-item-${i}`}
                  onClick={() => setLightboxIndex(i)}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: 0.05 * (i % 4) }}
                  className={`clip-corner-sm group relative overflow-hidden border border-white/10 text-left focus-visible:ring-2 focus-visible:ring-[#1EA7FF] ${
                    i === 0 ? "col-span-2 row-span-2" : ""
                  }`}
                >
                  <img
                    src={resolveImageUrl(g.url)}
                    alt={g.title}
                    className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                      i === 0 ? "aspect-square md:aspect-auto md:h-full" : "aspect-square"
                    }`}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#07080A]/80 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="absolute bottom-0 left-0 right-0 translate-y-2 p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                    <div className="font-display text-xl uppercase tracking-[0.06em] text-white">{g.title}</div>
                    {g.description && (
                      <div className="font-display text-sm uppercase tracking-[0.06em] text-white/70">
                        {g.description}
                      </div>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============ PLATFORMS ============ */}
      <section className="border-t border-white/10 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionHeading kicker={t("media.followKicker")} title={t("media.followTitle")} testId="media-platforms-heading" />
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            {PLATFORMS.map((p, i) => (
              <motion.a
                key={p.name}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`media-platform-${p.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * i }}
                className="light-sweep clip-corner group border border-white/10 bg-[#0F1217] p-6 transition-colors duration-200 hover:border-[#B7FF2A]/40"
              >
                <div className="flex items-center justify-between">
                  <div className="clip-corner-sm flex h-12 w-12 items-center justify-center bg-[#B7FF2A]/10 text-[#B7FF2A]">
                    <p.icon className="h-6 w-6" />
                  </div>
                  <ExternalLink className="h-4 w-4 text-white/25 transition-colors duration-300 group-hover:text-[#B7FF2A]" />
                </div>
                <div className="mt-4 font-display text-2xl uppercase tracking-[0.05em]">{p.name}</div>
                <div className="font-mono text-sm text-[#1EA7FF]">{p.handle}</div>
                <p className="mt-2 text-sm text-white/50">{t(p.descKey)}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ============ VIDEO LIGHTBOX ============ */}
      <AnimatePresence>
        {activeVideo && <VideoLightbox video={activeVideo} onClose={() => setActiveVideo(null)} />}
      </AnimatePresence>

      {/* ============ PHOTO LIGHTBOX ============ */}
      <Dialog open={isOpen} onOpenChange={(open) => !open && setLightboxIndex(null)}>
        <DialogContent
          data-testid="media-lightbox-dialog"
          aria-describedby={undefined}
          className="max-w-5xl border-white/10 bg-[#07080A]/95 p-2 sm:p-4 [&>button]:hidden"
        >
          <DialogTitle className="sr-only">
            {lightboxIndex !== null && gallery[lightboxIndex] ? gallery[lightboxIndex].title : t("media.galleryFallback")}
          </DialogTitle>
          {lightboxIndex !== null && gallery[lightboxIndex] && (
            <div className="relative">
              <img
                src={resolveImageUrl(gallery[lightboxIndex].url)}
                alt={gallery[lightboxIndex].title}
                className="max-h-[78vh] w-full rounded object-contain"
              />
              <div className="mt-3 flex items-center justify-between px-1">
                <div>
                  <div className="font-display text-xl uppercase tracking-[0.06em]">
                    {gallery[lightboxIndex].title}
                  </div>
                  {gallery[lightboxIndex].description && (
                    <div className="font-display text-sm uppercase tracking-[0.06em] text-white/70">
                      {gallery[lightboxIndex].description}
                    </div>
                  )}
                </div>
                <div className="font-mono text-xs text-white/40">
                  {lightboxIndex + 1} / {gallery.length}
                </div>
              </div>
              <button
                data-testid="media-lightbox-prev"
                onClick={prev}
                className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#07080A]/70 text-white transition-colors hover:bg-[#1EA7FF] hover:text-[#07080A]"
                aria-label={t("media.prev")}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                data-testid="media-lightbox-next"
                onClick={next}
                className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#07080A]/70 text-white transition-colors hover:bg-[#1EA7FF] hover:text-[#07080A]"
                aria-label={t("media.next")}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <button
                data-testid="media-lightbox-close"
                onClick={() => setLightboxIndex(null)}
                className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-[#07080A]/70 text-white transition-colors hover:bg-[#FF3B3B]"
                aria-label={t("media.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageWrap>
  );
}
